import { ref } from 'vue';

// Cost estimation data (based on official monthly pricing)
const pricingData = {
  // GCP A2 High GPU (CPU + memory only, excluding GPU)
  'a2-highgpu-1g': { compute: 692.06 },
  'a2-highgpu-2g': { compute: 1384.12 },
  'a2-highgpu-4g': { compute: 2768.24 },
  // GCP E2 series
  'e2-micro': { compute: 7.84 },
  'e2-small': { compute: 15.69 },
  'e2-medium': { compute: 31.38 },
  'e2-standard-16': { compute: 502.04 },
  'e2-standard-32': { compute: 1004.08 },
  'e2-highcpu-16': { compute: 371.02 },
  'e2-highcpu-32': { compute: 742.04 },
  // GCP N1 series
  'n1-standard-16': { compute: 711.77 },
  'n1-standard-32': { compute: 1423.54 },
  // GCP G2 series (L4 GPU)
  'g2-standard-16': { compute: 550.45 },
  'g2-standard-32': { compute: 1100.90 },

  // AWS T2 series (Burstable Performance)
  't2.micro': { compute: 8.47 },
  't2.small': { compute: 16.94 },
  't2.medium': { compute: 33.88 },
  't2.large': { compute: 67.76 },
  't2.xlarge': { compute: 135.52 },
  't2.2xlarge': { compute: 271.04 },

  // AWS T3 series (Burstable Performance)
  't3.micro': { compute: 7.60 },
  't3.small': { compute: 15.20 },
  't3.medium': { compute: 30.40 },
  't3.large': { compute: 60.80 },
  't3.xlarge': { compute: 121.60 },
  't3.2xlarge': { compute: 243.20 },

  // AWS M5 series (General Purpose)
  'm5.large': { compute: 69.12 },
  'm5.xlarge': { compute: 138.24 },
  'm5.2xlarge': { compute: 276.48 },
  'm5.4xlarge': { compute: 552.96 },
  'm5.8xlarge': { compute: 1105.92 },
  'm5.12xlarge': { compute: 1658.88 },
  'm5.16xlarge': { compute: 2211.84 },
  'm5.24xlarge': { compute: 3317.76 },

  // AWS C5 series (Compute Optimized)
  'c5.large': { compute: 61.44 },
  'c5.xlarge': { compute: 122.88 },
  'c5.2xlarge': { compute: 245.76 },
  'c5.4xlarge': { compute: 491.52 },
  'c5.9xlarge': { compute: 1105.92 },
  'c5.12xlarge': { compute: 1474.56 },
  'c5.18xlarge': { compute: 2211.84 },
  'c5.24xlarge': { compute: 2949.12 },

  // AWS R5 series (Memory Optimized)
  'r5.large': { compute: 100.80 },
  'r5.xlarge': { compute: 201.60 },
  'r5.2xlarge': { compute: 403.20 },
  'r5.4xlarge': { compute: 806.40 },
  'r5.8xlarge': { compute: 1612.80 },
  'r5.12xlarge': { compute: 2419.20 },
  'r5.16xlarge': { compute: 3225.60 },
  'r5.24xlarge': { compute: 4838.40 }
};

// GPU pricing (monthly)
const gpuPricing = {
  // GCP GPU pricing
  'nvidia-tesla-a100': 2264.14, // Single card monthly price
  'nvidia-l4': 525.02,        // Reference monthly price
  'nvidia-tesla-t4': 270.10,  // Reference monthly price

  // AWS GPU pricing
  'g4dn.xlarge': 525.02,      // T4 GPU instance
  'p3.2xlarge': 2264.14,      // V100 GPU instance
  'p4d.24xlarge': 2264.14     // A100 GPU instance
};

// Storage pricing (per GB per month)
const storagePricing = {
  gcp: 0.13,  // GCP Balanced persistent disk
  aws: 0.10   // AWS gp3 EBS volume (ap-northeast-1)
};

export function useCostEstimation() {
  const monthlyCost = ref(null);

  // Calculate cost based on machine type, GPU, and storage
  const calculateCost = (machineType, gpuType, gpuCount, diskSizeGb = 0, cloudProvider = 'gcp') => {
    if (!machineType) {
      monthlyCost.value = null;
      return;
    }

    const computeCost = pricingData[machineType]?.compute || 0;
    const storagePricePerGB = storagePricing[cloudProvider] || storagePricing.gcp;
    const storageCost = diskSizeGb * storagePricePerGB;

    let gpuCost = 0;
    if (gpuType && gpuCount) {
      gpuCost = gpuPricing[gpuType] * gpuCount;
    }

    const totalCost = computeCost + gpuCost + storageCost;

    monthlyCost.value = {
      compute: computeCost,
      gpu: gpuCost,
      storage: storageCost,
      total: totalCost
    };
  };

  // Get pricing data for external use
  const getPricingData = () => pricingData;
  const getGpuPricing = () => gpuPricing;
  const getStoragePricePerGB = (cloudProvider = 'gcp') => storagePricing[cloudProvider] || storagePricing.gcp;

  return {
    monthlyCost,
    calculateCost,
    getPricingData,
    getGpuPricing,
    getStoragePricePerGB
  };
}
